import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { useRoute } from "vue-router";
import { mapState } from "pinia";

import { useApp } from "../../App/useApp";
import config from "../../../common/config";
import { CentralIntakeService } from "../../../services/central-intake.service";
import { ToastrService } from "../../../services/toastr.service";
import { EnumsService } from "../../../services/enums.service";
import { PermissionManager } from "../../../common/utilities/permission-manager";
import { BranchesService } from "../../../services/branches-service";
import { EnumMap } from "../../../common/enum-map";
import {
    ApplicationEnum,
    ApplicationNameEnum,
    ParentPermissionEnum,
    Gender,
    PermissionActionEnum
} from "../../../enums/enums";
import { IBranchResponse } from "../../../resources-vue/vue-interfaces/i-branch";
import { PAGE_FAIL_RESPONSE } from "../../../resources/elements/pagination";
import { IGetReferralResult } from "../../../resources-vue/vue-interfaces/i-get-referral";
import { IPageCallbackResult } from "../../../resources-vue/vue-interfaces/i-page-container";
import { IMultiSelectDropDownOption } from "../../../resources-vue/vue-elements/MultiSelect/MultiSelect";
import { IGetAgency, IGetOrganization } from "../../../services/agency.service";
import { DownloadService } from "../../../services/download.service";

import ListLayout from "../../../resources-vue/vue-custom-elements/ListLayout/ListLayout.vue";
import BranchDefaultFilter from "../../../resources-vue/vue-custom-elements/BranchDefaultFilter/BranchDefaultFilter.vue";
import Pagination from "../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import SortableTh from "../../../resources-vue/vue-custom-elements/SortableTh/SortableTh.vue";
import ErrorMessageHandler from "../../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import CentralReferralRow from "./CentralReferralRow.vue";
import MultiSelect from "../../../resources-vue/vue-elements/MultiSelect/MultiSelect.vue";
import StaticTableFormater from "../../../resources-vue/vue-custom-elements/StaticTableFormater/StaticTableFormater.vue";

export default defineComponent({
    components: {
        ListLayout,
        BranchDefaultFilter,
        Pagination,
        SortableTh,
        ErrorMessageHandler,
        CentralReferralRow,
        MultiSelect,
        StaticTableFormater
    },

    data() {
        return {
            term: "",
            searchTimer: null,
            _bindingEngine: null,
            _centralIntakeService: null,
            _enumsService: null,
            _branchService: null,
            _permissionManager: null,
            _downloadService: null,
            _toastrService: null,
            sortBy: "referralDate",
            sortOrder: "desc",
            isError: false,
            columns: 12,
            router: null,
            page: null,
            isLoading: false,
            routeTerm: "",
            referralListPagination: null,
            hideTitle: false,
            selectedBranches: [],
            agencyMultiSelectOptions: {} as IMultiSelectDropDownOption,
            selectedAgencies: [] as string[],
            selectedAgenciesName: [] as string[],
            selectOrganization: [] as IGetOrganization[],
            agencyList: [] as IGetAgency[],
            branchRef: null,
            genderTypesEnum: [],
            lineOfServiceEnumMap: null,
            lineOfServiceEnum: [],
            canViewHospiceReferral: false,
            canViewPalliativeReferral: false,
            branches: [],
            referralsEditable: [],
            referralsDeleteable: [],
            responseHandler: {
                data: null,
                stamp: null,
                requests: []
            },
            excelExportPermission: false,
            nonAdmitReasonsData: {} as any,
            pageSize: 10,
            pageNumber: 1,
            route: useRoute(),
            dynamicTableToggled: false
        };
    },

    async created() {
        this._centralIntakeService = Container.instance.get(CentralIntakeService);
        this._enumsService = Container.instance.get(EnumsService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._branchService = Container.instance.get(BranchesService);
        this._downloadService = Container.instance.get(DownloadService);
        this._toastrService = Container.instance.get(ToastrService);
        this.lineOfServiceEnum = await this._enumsService.getLineOfService();
        this.branches = await this._branchService.getAllBranches();
        this.lineOfServiceEnumMap = new EnumMap(this.lineOfServiceEnum);
        this.genderTypesEnum = Array.from(Gender.enumWithDescriptions.values());
        this.referralListPagination = this.$refs?.referralListPagination as typeof Pagination;
        this.isLoading = true;
    },

    async mounted() {
        if (this.$route.query.term) {
            this.term = this.$route.query.term as string;
        }
        const organizationId = localStorage.getItem(config.organizationId);
        this.selectOrganization = this.organizations.filter((organization) => organization.id == organizationId);

        const selectedAgencies = this.selectOrganization[0].agencies;
        this.agencyList = selectedAgencies.map((agency) => {
            let agencyAndApplicationIds = agency.id.split("_");
            let application = parseInt(agencyAndApplicationIds[1], 10);

            return {
                ...agency,
                agencyId: agencyAndApplicationIds[0],
                application: application
            };
        });
        this.branches = await this._branchService.getAllBranches();
        this.referralListPagination = this.$refs?.referralListPagination as typeof Pagination;
        this.referralsEditable = this.branches.filter((branch) => this.isReferralEditAllowed(branch));
        this.referralsDeleteable = this.branches.filter((branch) => this.isReferralDeleteAllowed(branch));
        await this.setAgencyMultiSelect();
        if (this.$route.query.agencies) {
            const agencies = (this.$route.query.agencies as string).split(",");
            this.updateBranchesbyAgency(agencies);
        }
    },

    computed: {
        ...mapState(useApp, ["organizations"]),
        hasData() {
            return this.page?.itemCount > 0;
        },

        loadingCount() {
            return this.page?.items?.length > 0 ? this.page.items.length : config.pageSize;
        }
    },

    watch: {
        term() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(
                (term) => {
                    if (term?.length > 2 || term === "") {
                        this.search();
                        this.$router.replace({ query: { ...this.route.query, term: this.term } });
                    }
                },
                500,
                this.term
            );
        },

        selectedBranches: {
            handler() {
                this.pageReset();
            },
            deep: true
        }
    },

    methods: {
        search() {
            if (!this.isLoading) {
                this.pageReset();
            }
        },

        isReferralEditAllowed(branch: IBranchResponse): boolean {
            if (!branch.application || !branch.accountId) return false;
            return this._permissionManager.checkPermission(
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canEdit,
                branch.accountId
            );
        },

        isReferralDeleteAllowed(branch: IBranchResponse): boolean {
            if (!branch.application || !branch.accountId) return false;
            return this._permissionManager.checkPermission(
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canDelete,
                branch.accountId
            );
        },

        updateBranches(branches: string[]) {
            this.selectedBranches.length = 0;
            this.selectedBranches.push(...branches);
            let branchesData = this.branches.filter((branch) => this.selectedBranches?.includes(branch.id));
            const selectedAgenciesId = [] as any[];
            this.agencyList.filter((agency) => {
                branchesData.some((branch) => {
                    const { application, accountId, providerId } = branch;
                    if (
                        (application == ApplicationEnum.HomeCare || application == ApplicationEnum.AgencyCore) &&
                        agency.id.includes(accountId)
                    ) {
                        selectedAgenciesId.push(agency.id);
                        return true;
                    }
                    if (
                        (application == ApplicationEnum.AxxessHospiceFE ||
                            application == ApplicationEnum.AxxessPalliative) &&
                        agency.id.includes(accountId) &&
                        agency.id.includes(providerId)
                    ) {
                        selectedAgenciesId.push(agency.id);
                        return true;
                    }
                    return false;
                });
            });
            this.canShowexcelExport(selectedAgenciesId);
        },

        async setAgencyMultiSelect() {
            try {
                this.agencyMultiSelectOptions = {};
                this.selectedAgencies.length = 0;

                if (!this.agencyList || this.agencyList?.length === 0) {
                    return;
                }
                let allowedProducts = [
                    ApplicationEnum.AgencyCore,
                    ApplicationEnum.HomeCare,
                    ApplicationEnum.AxxessHospice,
                    ApplicationEnum.AxxessHospiceFE,
                    ApplicationEnum.AxxessPalliative
                ];
                const schedulerViewPermissions = this._permissionManager.getPermmission(
                    ParentPermissionEnum.referrals,
                    PermissionActionEnum.canView
                );
                let schedulerAgencies = schedulerViewPermissions
                    .filter((item: any) => {
                        if (!item) {
                            return false;
                        }
                        let application = item.application;
                        if (application === ApplicationEnum.AxxessHospiceFE) {
                            application = ApplicationEnum.AxxessHospice;
                        }
                        return allowedProducts.includes(application);
                    })
                    .map((x: any) => x.agencyId);
                let filterableProducts = this.agencyList.filter((agency) => {
                    let isAllowed = allowedProducts.includes(agency.application);
                    return isAllowed && schedulerAgencies.includes(agency.agencyId);
                });
                filterableProducts?.forEach((agency) => {
                    let appName =
                        ApplicationNameEnum[
                            agency.application === ApplicationEnum.AxxessHospiceFE
                                ? ApplicationEnum.AxxessHospice
                                : agency.application
                        ];

                    if (!this.agencyMultiSelectOptions[appName]) {
                        this.agencyMultiSelectOptions[appName] = [];
                    }

                    this.agencyMultiSelectOptions[appName].push({
                        displayText: agency.name,
                        value: agency.id
                    });
                });

                Object.values(this.agencyMultiSelectOptions).forEach((agency: any) => {
                    let values = agency.map((item: any) => item.value);
                    this.selectedAgencies.push(...values);
                });
                this.canShowexcelExport(this.selectedAgencies);
                // For select agencies change
            } catch (e) {
                console.warn(e);
                // Silent fail
            }
        },

        updateBranchesbyAgency(agencies: string[]) {
            this.selectedAgencies.length = 0;
            this.selectedAgencies.push(...agencies);
            this.$router.replace({
                query: { term: this.term ? this.term : "", agencies: this.selectedAgencies.join(",") }
            });
            this.canShowexcelExport(this.selectedAgencies);
            this.selectedAgenciesName = this.selectOrganization[0].agencies
                .filter((agency) => this.selectedAgencies.includes(agency.id))
                .map((agency) => agency.name);
        },

        canShowexcelExport(selectedAgencies: string[]) {
            this.excelExportPermission = selectedAgencies.some((agency) => {
                let agencyData = this.agencyList.find((agn) => agn.id === agency);
                const action =
                    agencyData.application == ApplicationEnum.HomeCare ||
                    agencyData.application == ApplicationEnum.AgencyCore
                        ? PermissionActionEnum.canExport
                        : agencyData.application == ApplicationEnum.AxxessPalliative ||
                            agencyData.application == ApplicationEnum.AxxessHospiceFE
                          ? PermissionActionEnum.canView
                          : "";
                return (
                    action &&
                    this._permissionManager.checkPermission(ParentPermissionEnum.referrals, action, agencyData.agencyId)
                );
            });
        },

        async downloadChart() {
            try {
                const payLoad = {
                    page: this.pageNumber,
                    pageLength: this.pageSize.toString(),
                    sortBy: this.sortBy,
                    order: this.sortOrder,
                    term: this.term,
                    locationIds: this.selectedBranches,
                    agencyIds: this.selectedAgencies.map((agency) => agency.split("_")[0]),
                    Export: true
                };
                const response = await this._centralIntakeService.exportReferrals(payLoad);
                const warningMessage = response.headers.get("x-warning-message");
                if (warningMessage) {
                    this._toastrService.warning({
                        title: `Export Excel Warning`,
                        message: warningMessage
                    });
                }
                this._downloadService.downloadFile(response, response.type);
                this._toastrService.success({
                    title: `Success`,
                    message: `Referral Export Excel downloaded.`
                });
            } catch (error) {
                this._toastrService.error({
                    title: `Error`,
                    message: "There was a problem while Export Excel of the referrals. Please try again."
                });
            }
        },

        pageReset() {
            this.referralListPagination?.reset();
        },

        pageRefresh() {
            this.referralListPagination?.getData();
        },

        async pageChangedCallback(pageNumber: number, pageSize: number): Promise<IPageCallbackResult> {
            this.pageNumber = pageNumber;
            this.pageSize = pageSize;
            if (this.selectedBranches.length === 0) {
                this.page = null;
                return PAGE_FAIL_RESPONSE;
            }
            try {
                let callTime = Date.now();
                this.responseHandler.stamp = callTime;
                this.isLoading = true;
                this.responseHandler.data = null;
                this.isError = false;
                let payLoad = {
                    page: pageNumber,
                    pageLength: pageSize,
                    sortBy: this.sortBy,
                    order: this.sortOrder,
                    term: this.term,
                    locationIds: this.selectedBranches,
                    agencyIds: this.selectedAgencies.map((agency) => agency.split("_")[0]),
                    // Flag to get only non admit referrals
                    getNonAdmitReferral: false
                };
                let response = this._centralIntakeService.referrals(payLoad);
                this.responseHandler.requests.push(response);
                let data: IGetReferralResult = await response;
                if (this.responseHandler.stamp == callTime) {
                    this.responseHandler.data = data;
                } else {
                    data =
                        this.responseHandler.data ||
                        (await this.responseHandler.requests[this.responseHandler.requests.length - 1]);
                }
                this.page = data;
                return {
                    totalPageCount: data.pageCount,
                    success: data.itemCount > 0,
                    totalCount: data.itemCount
                };
            } catch (e) {
                this.isError = true;
                return PAGE_FAIL_RESPONSE;
            } finally {
                this.isLoading = false;
            }
        },

        newReferral() {
            this.$router.push("/axxess-central/create/interim/referral");
        },

        updatePageSizeChanged(value: number) {
            this.pageSize = value;
        },

        sortByUpdated(value: string) {
            if (value != this.sortBy) {
                this.sortBy = value;
                this.pageReset();
            }
        },

        sortOrderUpdated(value: string) {
            if (value != this.sortOrder) {
                this.sortOrder = value;
                this.pageReset();
            }
        },

        getGenderTypes(id: number) {
            let genderType = this.genderTypesEnum.filter((value, index) => value.value == id)[0];
            if (genderType) {
                return genderType.name ? genderType.name : "";
            } else {
                return "";
            }
        },

        resetList() {
            this.isError = false;
            this.pageReset();
        },

        updateNonAdmitReasonsData(reasons: any, referral: any) {
            this.nonAdmitReasonsData[referral.accountId] = reasons;
        },

        getCustomSettings() {
            return [
                { Width: 120, RowIndex: 1 },
                { Width: 128, RowIndex: 2 },
                { Width: 100, RowIndex: 8 }
            ];
        }
    }
});
